import { createTheme } from '@mui/material/styles';

// Berry Dashboard inspired color palette
const berryColors = {
  // Primary colors
  primaryLight: '#e3f2fd',
  primaryMain: '#2196f3',
  primaryDark: '#1e88e5',
  primary200: '#90caf9',
  primary800: '#1565c0',
  
  // Secondary colors
  secondaryLight: '#ede7f6',
  secondaryMain: '#673ab7',
  secondaryDark: '#5e35b1',
  secondary200: '#b39ddb',
  secondary800: '#4527a0',
  
  // Success colors
  successLight: '#b9f6ca',
  success200: '#69f0ae',
  successMain: '#00e676',
  successDark: '#00c853',
  
  // Error colors
  errorLight: '#ef9a9a',
  errorMain: '#f44336',
  errorDark: '#c62828',
  
  // Warning colors
  warningLight: '#fff8e1',
  warningMain: '#ffe57f',
  warningDark: '#ffc107',
  
  // Orange colors
  orangeLight: '#fbe9e7',
  orangeMain: '#ffab91',
  orangeDark: '#d84315',
  
  // Grey colors
  grey50: '#fafafa',
  grey100: '#f5f5f5',
  grey200: '#eeeeee',
  grey300: '#e0e0e0',
  grey500: '#9e9e9e',
  grey600: '#757575',
  grey700: '#616161',
  grey900: '#212121',
  
  // Paper
  paper: '#ffffff'
};

// Berry-inspired theme for React Admin
export const berryTheme = createTheme({
  palette: {
    primary: {
      light: berryColors.primaryLight,
      main: berryColors.primaryMain,
      dark: berryColors.primaryDark,
    },
    secondary: {
      light: berryColors.secondaryLight,
      main: berryColors.secondaryMain,
      dark: berryColors.secondaryDark,
    },
    success: {
      light: berryColors.successLight,
      main: berryColors.successMain,
      dark: berryColors.successDark,
    },
    error: {
      light: berryColors.errorLight,
      main: berryColors.errorMain,
      dark: berryColors.errorDark,
    },
    warning: {
      light: berryColors.warningLight,
      main: berryColors.warningMain,
      dark: berryColors.warningDark,
    },
    background: {
      default: berryColors.grey50,
      paper: berryColors.paper,
    },
    text: {
      primary: berryColors.grey700,
      secondary: berryColors.grey500,
    },
  },
  typography: {
    fontFamily: '"Product Sans", ui-rounded, "Google Sans", -apple-system, system-ui, "Helvetica", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    
    h1: {
      fontSize: '2.125rem', // 34px
      fontWeight: 600,
      lineHeight: 1.235,
      letterSpacing: '-0.00735em',
      color: berryColors.grey900,
      marginBottom: '16px',
    },
    h2: {
      fontSize: '1.75rem', // 28px
      fontWeight: 600,
      lineHeight: 1.286,
      letterSpacing: '-0.00214em',
      color: berryColors.grey900,
      marginBottom: '14px',
    },
    h3: {
      fontSize: '1.5rem', // 24px
      fontWeight: 600,
      lineHeight: 1.334,
      letterSpacing: '0em',
      color: berryColors.grey900,
      marginBottom: '12px',
    },
    h4: {
      fontSize: '1.25rem', // 20px
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0.00735em',
      color: berryColors.grey900,
      marginBottom: '10px',
    },
    h5: {
      fontSize: '1.125rem', // 18px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0em',
      color: berryColors.grey900,
      marginBottom: '8px',
    },
    h6: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
      color: berryColors.grey900,
      marginBottom: '8px',
    },
    subtitle1: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: 1.75,
      letterSpacing: '0.00938em',
      color: berryColors.grey700,
    },
    subtitle2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
      color: berryColors.grey700,
    },
    body1: {
      fontSize: '1rem', // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
      color: berryColors.grey700,
    },
    body2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
      color: berryColors.grey600,
    },
    button: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
      color: berryColors.grey500,
    },
    overline: {
      fontSize: '0.75rem', // 12px
      fontWeight: 500,
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
      color: berryColors.grey500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    // Enhanced Card styling
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 14px 0 rgb(32 40 45 / 8%)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 20px 0 rgb(32 40 45 / 16%)',
          },
          borderRadius: 12,
        },
      },
    },
    // Enhanced Paper styling  
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 14px 0 rgb(32 40 45 / 8%)',
        },
        elevation1: {
          boxShadow: '0 2px 14px 0 rgb(32 40 45 / 8%)',
        },
        elevation2: {
          boxShadow: '0 4px 20px 0 rgb(32 40 45 / 16%)',
        },
      },
    },
    // Enhanced Button styling
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 24px',
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: 1.75,
        },
        contained: {
          boxShadow: '0 2px 14px 0 rgb(32 40 45 / 8%)',
          '&:hover': {
            boxShadow: '0 4px 20px 0 rgb(32 40 45 / 16%)',
          },
        },
      },
    },
    // Enhanced Table styling
    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableHead-root': {
            '& .MuiTableCell-head': {
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08333em',
              color: berryColors.grey600,
              backgroundColor: berryColors.grey50,
              borderBottom: `1px solid ${berryColors.grey200}`,
              padding: '16px',
            },
          },
          '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
              '&:hover': {
                backgroundColor: berryColors.grey50,
              },
              '& .MuiTableCell-body': {
                fontSize: '0.875rem',
                fontWeight: 400,
                color: berryColors.grey700,
                padding: '16px',
                borderBottom: `1px solid ${berryColors.grey200}`,
              },
            },
          },
        },
      },
    },
    // Enhanced AppBar styling
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 14px 0 rgb(32 40 45 / 8%)',
          backgroundColor: berryColors.paper,
          color: berryColors.grey900,
        },
      },
    },
    // Enhanced Drawer styling
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: berryColors.paper,
          borderRight: `1px solid ${berryColors.grey200}`,
        },
      },
    },
    // Enhanced form inputs
    MuiTextField: {
      styleOverrides: {
        root: {
          width: '100%', // Ensure full width
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: berryColors.paper,
            width: '100%', // Full width for input
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: berryColors.primaryMain,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: berryColors.primaryMain,
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root': {
            color: berryColors.grey600,
            transform: 'translate(14px, 16px) scale(1)', // Proper label positioning
            '&.Mui-focused': {
              color: berryColors.primaryMain,
              transform: 'translate(14px, -9px) scale(0.75)',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          width: '100%', // Full width
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: berryColors.grey300,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: berryColors.primaryMain,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: berryColors.primaryMain,
            borderWidth: 2,
          },
        },
        input: {
          padding: '16px 14px', // Consistent padding
          width: '100%',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: berryColors.grey600,
          '&.Mui-focused': {
            color: berryColors.primaryMain,
          },
        },
        outlined: {
          transform: 'translate(14px, 16px) scale(1)', // Centered label positioning
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
    },
    // Enhanced Typography components
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: '2.125rem',
          fontWeight: 600,
          lineHeight: 1.235,
          letterSpacing: '-0.00735em',
          color: berryColors.grey900,
          marginBottom: '16px',
        },
        h2: {
          fontSize: '1.75rem',
          fontWeight: 600,
          lineHeight: 1.286,
          letterSpacing: '-0.00214em',
          color: berryColors.grey900,
          marginBottom: '14px',
        },
        h3: {
          fontSize: '1.5rem',
          fontWeight: 600,
          lineHeight: 1.334,
          letterSpacing: '0em',
          color: berryColors.grey900,
          marginBottom: '12px',
        },
        h4: {
          fontSize: '1.25rem',
          fontWeight: 600,
          lineHeight: 1.4,
          letterSpacing: '0.00735em',
          color: berryColors.grey900,
          marginBottom: '10px',
        },
        h5: {
          fontSize: '1.125rem',
          fontWeight: 600,
          lineHeight: 1.5,
          letterSpacing: '0em',
          color: berryColors.grey900,
          marginBottom: '8px',
        },
        h6: {
          fontSize: '1rem',
          fontWeight: 600,
          lineHeight: 1.6,
          letterSpacing: '0.0075em',
          color: berryColors.grey900,
          marginBottom: '8px',
        },
      },
    },
  },
});

export default berryTheme;